<template>
  <div ref="rectEl" :style="rectStyle" class="rectangle-wrapper">
    <svg
      :width="svgW"
      :height="svgH"
      overflow="visible"
      :viewBox="`0 0 ${svgW} ${svgH}`"
      style="position: absolute; top: 0; left: 0"
    >
      <rect
        :x="bleedPx"
        :y="bleedPx"
        :width="wPx"
        :height="hPx"
        :rx="rxPx"
        :ry="ryPx"
        fill="none"
        stroke="black"
        stroke-width="2"
      />
      <rect
        v-if="bleedPx > 0"
        :x="0"
        :y="0"
        :width="svgW"
        :height="svgH"
        :rx="rxPx + bleedPx"
        :ry="ryPx + bleedPx"
        fill="none"
        stroke="#F79009"
        stroke-width="2"
        stroke-dasharray="6,4"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  defineExpose,
  defineProps,
  watchEffect,
  type CSSProperties,
  defineEmits,
} from 'vue'
import { useCanvasUnits } from '@/composables/useCanvasUnits.ts'
import { useDesignerStore } from '@/stores/designerStore.ts'

const props = defineProps<{
  width: number
  height: number
  positionX: number
  positionY: number
  radiusX: number
  radiusY: number
  zIndex?: number
  bleedOffset?: number
}>()

const emit = defineEmits([
  'update:width',
  'update:height',
  'update:radiusX',
  'update:radiusY',
  'update:positionX',
  'update:positionY',
  'update:svg',
])

const { mmToPx } = useCanvasUnits()
const designerStore = useDesignerStore()
const rectEl = ref<HTMLElement | null>(null)

watchEffect(() => {
  // keď práve hydratujeme zo servera, nič neprepočítavaj
  if (designerStore.hydrating) {
    // ale stále pošli svg, ak chceš mať hneď k dispozícii
    emit('update:svg', getSvg())
    return
  }

  const MAX_SHORT = 300
  const MAX_LONG = 400

  let newW = props.width
  let newH = props.height

  const isWidthShorter = newW <= newH

  // Kratší rozmer nesmie presiahnuť 300
  if (isWidthShorter && newW > MAX_SHORT) {
    newW = MAX_SHORT
    newH = Math.min(newH, MAX_SHORT)
  } else if (!isWidthShorter && newH > MAX_SHORT) {
    newH = MAX_SHORT
    newW = Math.min(newW, MAX_SHORT)
  }

  // Dlhší rozmer nesmie presiahnuť 400
  if (isWidthShorter && newH > MAX_LONG) {
    newH = MAX_SHORT
  } else if (!isWidthShorter && newW > MAX_LONG) {
    newW = MAX_SHORT
  }

  // Emituj zmeny len ak je potrebné
  if (newW !== props.width) emit('update:width', newW)
  if (newH !== props.height) emit('update:height', newH)

  // Radius
  const maxRadiusX = newW / 2
  const maxRadiusY = newH / 2

  if (props.radiusX < 0 || props.radiusX > maxRadiusX) {
    emit('update:radiusX', Math.max(0, Math.min(props.radiusX, maxRadiusX)))
  }
  if (props.radiusY < 0 || props.radiusY > maxRadiusY) {
    emit('update:radiusY', Math.max(0, Math.min(props.radiusY, maxRadiusY)))
  }

  // Centrovanie
  const canvasW = designerStore.canvasSizeInMm.width
  const canvasH = designerStore.canvasSizeInMm.height
  emit('update:positionX', +(canvasW / 2 - newW / 2).toFixed(2));
  emit('update:positionY', +(canvasH / 2 - newH / 2).toFixed(2));
  emit('update:svg', getSvg());
})

const wPx = computed(() => mmToPx(props.width, 'x'))
const hPx = computed(() => mmToPx(props.height, 'y'))
const rxPx = computed(() => mmToPx(props.radiusX, 'x'))
const ryPx = computed(() => mmToPx(props.radiusY, 'y'))
const bleedPx = computed(() => mmToPx(props.bleedOffset || 0, 'x'))

const svgW = computed(() => wPx.value + bleedPx.value * 2)
const svgH = computed(() => hPx.value + bleedPx.value * 2)

const rectStyle = computed<Partial<CSSProperties>>(() => {
  void designerStore.scaleTick;

  return {
    position: 'absolute',
    left: `${mmToPx(props.positionX, 'x') - bleedPx.value}px`,
    top: `${mmToPx(props.positionY, 'y') - bleedPx.value}px`,
    width: `${svgW.value}px`,
    height: `${svgH.value}px`,
    pointerEvents: 'none',
    zIndex: `${props.zIndex ?? 10}`,
  }
})

function getSvg() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${props.width}mm" height="${props.height}mm" viewBox="0 0 ${props.width} ${props.height}">
    <rect width="${props.width}" height="${props.height}" rx="${props.radiusX}" ry="${props.radiusY}" fill="none" stroke="black" stroke-width="0.5"/>
  </svg>`
}

defineExpose({ rectEl, getSvg })
</script>

<style scoped>
.rectangle-wrapper {
  /* Bleed je už vo vnútri SVG */
}
</style>
